import { MinusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import calculateBookingStatus from 'library/helpers/bookingHelpers';
import SuperFetch from 'library/helpers/superFetch';
import WithHyperLink from 'library/hoc/withHyperLink';
import { useGetPersonDisplayName } from 'library/hooks';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Loader } from 'zComponents/atoms';
import { Row, Table, Typography } from 'zui/Antd';
import BookingActionsDropdown from '../BookingActionsDropdown';
const { Text } = Typography;

const BookingTable = ({ bookingData, previewBooking, setPreviewBooking, deviceID }) => {
	const { data: bookingDataFromRedux } = useSelector((state) => state.Booking);
	const displayNames = useGetPersonDisplayName(true);
	const {
		remoteConfig: { bookingStatusType }
	} = useSelector((state) => state.FbRemoteConfig);

	const [bookings, setBookings] = useState([]);
	const [select, setSelect] = useState([0]);
	const [loading, setLoading] = useState(false);

	const rowSelection = {
		type: 'radio',
		selectedRowKeys: select,
		visible: false,
		onChange: (selectedRowKeys) => {
			setSelect(selectedRowKeys);
		}
	};

	const NavigateWithLink = WithHyperLink((a) => {
		return <p>{a.children}</p>;
	});

	const getBookingByDevice = useCallback(async () => {
		if (deviceID) {
			setLoading(true);
			try {
				const res = await SuperFetch.get(
					`/booking`,
					{},
					{
						deviceID: deviceID,
						deleted: true,
						completed: true
					}
				);
				setBookings(res.data);
				setLoading(false);
			} catch (error) {
				setBookings([]);
				setLoading(false);
			}
		}
	}, [deviceID]);

	useEffect(() => {
		getBookingByDevice();
	}, [getBookingByDevice, bookingDataFromRedux]);

	useEffect(() => {
		if (bookingData) {
			setBookings(bookingData);
			setPreviewBooking(bookingData[select]);
		}
	}, [bookingData, select, setPreviewBooking]);

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: 130,
			ellipsis: true
		},
		{
			title: 'Type',
			dataIndex: ['bookingType', 'name'],
			key: 'bookingType',
			width: 75,
			ellipsis: true,
			sorter: (a, b) => a.device.name.localeCompare(b.device.name)
		},
		{
			title: 'Status',
			key: 'name',
			width: 90,
			ellipsis: true,
			filters: [
				{ text: bookingStatusType[1].title, value: bookingStatusType[1].title },
				{ text: bookingStatusType[2].title, value: bookingStatusType[2].title },
				{ text: bookingStatusType[3].title, value: bookingStatusType[3].title },
				{ text: bookingStatusType[4].title, value: bookingStatusType[4].title }
			],
			onFilter: (value, record) =>
				calculateBookingStatus(
					record.startTime,
					record.endTime,
					record.deletedDate,
					true,
					bookingStatusType
				) === value,
			render: (props) =>
				calculateBookingStatus(props.startTime, props.endTime, props.deletedDate, false, bookingStatusType)
		},
		{
			title: 'Device',
			dataIndex: ['device'],
			key: 'device',
			width: 120,
			ellipsis: true,
			sorter: (a, b) => a.device.name.localeCompare(b.device.name),
			render: (record) => (
				<NavigateWithLink type={'device'} dataID={record.uID}>
					{record.name}
				</NavigateWithLink>
			)
		},
		{
			title: 'Person',
			dataIndex: ['person'],
			key: 'person',
			width: 115,
			ellipsis: true,
			sorter: (a, b) => a.person.displayName.localeCompare(b.person.displayName),
			render: (record) => (
				<NavigateWithLink type={'person'} dataID={record.personID} title={displayNames[record.personID]?.title}>
					{displayNames[record.personID]?.displayName}
				</NavigateWithLink>
			)
		},
		{
			title: 'Time',
			width: 150,
			ellipsis: true,
			render: (_, props) => <GetTime startTime={props.startTime} endTime={props.endTime} />,
			sorter: (a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix()
		},
		{
			title: 'Actions',
			ellipsis: true,
			align: 'center',
			key: 'endTime',
			width: 100,
			render: (record) => {
				const bookingStatus = calculateBookingStatus(
					record.startTime,
					record.endTime,
					record.deletedDate,
					true,
					bookingStatusType
				);
				if (
					bookingStatus.includes(bookingStatusType[1].title) ||
					bookingStatus.includes(bookingStatusType[2].title)
				) {
					return (
						<BookingActionsDropdown
							data={record}
							key={record.bookingID}
							activeButton={record.bookingID === previewBooking?.bookingID}
						/>
					);
				} else {
					return <div />;
				}
			}
		}
	];

	if (!deviceID && !bookingData) return <></>;
	return (
		<Fragment>
			{loading ? (
				<Loader />
			) : (
				<Table
					columns={columns}
					pageSize={10}
					dataSource={bookings}
					rowSelection={!deviceID && rowSelection}
					onRow={(record) => ({
						onClick: () => {
							setSelect([record.key]);
							setPreviewBooking(record);
						}
					})}
				/>
			)}
		</Fragment>
	);
};
const GetTime = ({ startTime, endTime }) => {
	// Convert to local time and format
	const finalStartTime = dayjs.utc(startTime).local();
	const finalEndTime = dayjs.utc(endTime).local();

	return (
		<Row className='bookingTime'>
			<div>
				<Text>{finalStartTime.format('h:mm A')}</Text>
				<Text>{finalStartTime.format('ddd DD MMM')}</Text>
			</div>
			<div className='icon-minus'>
				<MinusOutlined />
			</div>
			<div>
				<Text>{finalEndTime.format('HH:mm A')}</Text>
				<Text>{finalEndTime.format('ddd DD MMM')}</Text>
			</div>
		</Row>
	);
};

export default BookingTable;
